import {
  getDatePeriodArray,
  getDayjs,
  getValidPeriodArray,
  isInDateRange,
  isStringWithOnlyNumbers,
  sortNumber,
  sortPeriod,
} from '../common/utils';
import { useWatch } from 'react-hook-form';
import { useAllClassifierCategoriesQuery } from '../classifier/query/useAllClassifierCategoriesQuery';
import { ClassifierGroupArrayType, ClassifierGroupType } from '../classifier';
import { PeriodArray } from '../common/types';
import {
  DataObjectRelationVersionType,
  DataObjectVersionType,
  VersionDataType,
} from './types';

const getVersionSortCode = (version: VersionDataType) => {
  switch (version.statusCode) {
    case 'ACTIVE':
      return 0;
    case 'INACTIVE':
      return 1;
    case 'DRAFT':
    default:
      return 2;
  }
};

export const getSortedVersions = (versions: Array<VersionDataType>) => {
  const vers = [...versions];
  vers.sort((a, b) => {
    const versionSortCodeA = getVersionSortCode(a);
    const versionSortCodeB = getVersionSortCode(b);
    if (versionSortCodeA === versionSortCodeB) {
      const sortPeriodValue = sortPeriod(
        getValidPeriodArray(a.validPeriod),
        getValidPeriodArray(b.validPeriod),
      );
      if (sortPeriodValue === 0) {
        return sortNumber(a.id, b.id);
      }
      return -sortPeriodValue;
    }

    return versionSortCodeA - versionSortCodeB;
  });
  return vers;
};

export const getActiveVersion = (
  versions: Array<VersionDataType>,
): VersionDataType => {
  return getSortedVersions(versions)[0];
};

export const getValuesFromString = (values: string[]): number[] | undefined => {
  return Array.from(
    new Set(
      values
        .flatMap((item) => item.split('-'))
        .filter((value) => Number.isSafeInteger(Number(value)))
        .map((value) => Number(value)),
    ),
  );
};

export const useClassifierData = (classifierCode: string) => {
  const { data = [] } = useAllClassifierCategoriesQuery(
    { classifierCode },
    {
      enabled: !!classifierCode,
    },
  );

  return { code: classifierCode, data };
};

export const useClassifierCategories = (field: string) => {
  const classifier = useWatch({
    name: field.replace('.constantValue', '.classifier'),
  });
  const code = classifier?.code;
  return useClassifierData(code ?? '');
};

export function filterClassifierPeriod(
  classifiers?: ClassifierGroupArrayType,
  period?: PeriodArray,
) {
  if (!period) {
    return classifiers ?? [];
  }

  return (
    classifiers?.filter((classifier) =>
      isInDateRange(
        getDatePeriodArray(classifier.validFrom, classifier.validTo),
        period,
      ),
    ) ?? []
  );
}

export function filterCategoryPeriod(
  classifier: ClassifierGroupType,
  period?: PeriodArray,
) {
  const { categories, validFrom, validTo } = classifier;
  if (!period) {
    return categories;
  }

  return categories?.filter((cat) =>
    isInDateRange(
      getDatePeriodArray(cat.validFrom ?? validFrom, cat.validTo ?? validTo),
      period,
    ),
  );
}

export const getGroupId = (groupAndSubgroupId?: string) => {
  const [groupId] = groupAndSubgroupId?.split('-') || [];
  if (groupId) {
    return Number(groupId);
  }

  return undefined;
};

export const replaceListObjectsId = (
  values?: { id?: any }[],
  defaultId?: any,
) => {
  values?.forEach((item) => (item.id = defaultId));
};

export function segmentSubField(path: string[]) {
  const parts: string[] = [];
  const subPath: string[] = ['segments'];
  const length = path.length;
  path.forEach((part, index) => {
    if (isStringWithOnlyNumbers(part)) {
      parts.push(subPath.join('.') + '.label');
      parts.push((Number(part) + 1).toString());
    } else {
      subPath.push(part);

      if (index === length - 1) {
        parts.push(subPath.join('.'));
      }
    }
  });

  return parts;
}

type VersionTypeWithMinorNumber = {
  versionNumber: number | null;
  minorNumber: number | null;
} & {
  [key: string]: any;
};

export const getLatestDataObjectVersion = (
  versions: VersionTypeWithMinorNumber[],
) =>
  versions.length > 0
    ? versions.reduce((max, curr) =>
        new Date(max.calculationTime) > new Date(curr.calculationTime)
          ? max
          : curr,
      )
    : null;

export function getLatestOutputVersionId(
  versions: DataObjectVersionType[] | DataObjectRelationVersionType[],
  valueDate?: string,
): number | undefined {
  if (versions.length === 0) {
    return undefined;
  }

  const filtered = valueDate
    ? versions.filter((v) => v.valueDate === valueDate)
    : versions;

  if (filtered.length === 0) {
    return undefined;
  }

  const latest = filtered.reduce((acc, ver) => {
    if (!ver.calculationTime) return acc;
    if (!acc.calculationTime) return ver;

    const verTime = getDayjs(ver.calculationTime);
    const accTime = getDayjs(acc.calculationTime);

    if (!verTime || !accTime) return acc;
    return verTime.isAfter(accTime) ? ver : acc;
  });

  return latest.id;
}
